import AddIcon from "@mui/icons-material/Add";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import ReplayIcon from "@mui/icons-material/Replay";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import { skipToken } from "@reduxjs/toolkit/query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import React, { useCallback, useEffect, useId, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { getUserCoworkersByUserIds } from "users/information/coworker-information/_stores/user-coworkers-redux-slice";
import UserCoworker from "users/information/coworker-information/user-coworker";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { getUpdatedProposalBuilderByProposalId } from "work/proposals/_store/proposals-redux-slice";
import {
  clearStagedReviewerSelection,
  proposalUncommittedInputScopeKey,
  readStagedReviewerSelection,
  writeStagedReviewerSelection,
} from "work/proposals/_store/proposal-uncommitted-input-store";
import Proposal from "work/proposals/proposal";
import useInfiniteProposalReviewerCandidates from "work/proposals/reviewers/_hooks/use-infinite-proposal-reviewer-candidates";
import type ProposalReviewerCandidate from "work/proposals/reviewers/proposal-reviewer-candidate";
import ReviewerMessagePopover from "work/proposals/reviewers/_views/reviewer-message-popover";
import type WorkAgreement from "work/work-agreements/work-agreement";
import ProposalReviewer from "../proposal-reviewer";
import {
  applySharedReviewerNote,
  followsSharedReviewerNote,
  describeReviewerChanges,
  getReviewerSendActionState,
  getReviewRequestRecipientUserIds,
  inferSharedReviewerNote,
  removeReviewer,
  setReviewerNote,
  toggleReviewerEditing,
  toggleReviewerSelection,
} from "work/proposals/reviewers/_views/reviewer-selection";
import LoadingButton from "_shared/_views/loading/loading-button";

/** The add field's placeholder, which the browser tests also use to find this popover. */
export const REVIEWER_SEARCH_PLACEHOLDER = "Search by name or email";
const RESULTS_LOAD_MORE_THRESHOLD_PX = 20;
const COMMENT_ACCESS = "comment";
const EDIT_ACCESS = "edit";
// The avatar gives up ListItemAvatar's own reserved width so a row's controls can have the space, so
// the row's gap is all that separates the avatar from the name — in the roster and the directory alike.
const REVIEWER_ROW_GAP = 1.5;
// One height and one inset for every person row, directory or roster, so the avatars sit in one column
// down the popover — the same column as the add field's left edge.
const REVIEWER_ROW_MIN_HEIGHT = "3.5rem";
// Neither list grows past four and a half rows on any screen, so the popover stays a popover rather
// than a page, and the cut-off row says there is more to scroll to. On a short screen both give up
// height before anything else does (see `PopoverContainer`).
const REVIEWER_LIST_MAX_HEIGHT = `calc(${REVIEWER_ROW_MIN_HEIGHT} * 4.5)`;

const ReviewerPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPopover-paper": {
    // A column, so the content below can be held to the paper's own max-height (the viewport, less a
    // margin) and share it out, rather than overflowing and scrolling the whole popover as one.
    display: "flex",
    flexDirection: "column",
    maxWidth: `calc(100vw - ${theme.spacing(2)})`,
    [theme.breakpoints.up("sm")]: {
      maxWidth: `calc(100vw - ${theme.spacing(4)})`,
    },
  },
}));
const PopoverContainer = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  maxWidth: `calc(100vw - ${theme.spacing(4)})`,
  // Everything in it keeps its height except the two lists, which scroll. On a phone the header, the
  // field and the footer stay put and the lists take what is left between them.
  minHeight: 0,
  minWidth: 0,
  "& > *": {
    flexShrink: 0,
  },
  width: "28rem",
  [theme.breakpoints.down("sm")]: {
    maxWidth: `calc(100vw - ${theme.spacing(2)})`,
  },
}));
const Header = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(2, 1, 0, 2),
}));
/** The pending reviewers under the heading, inset as the heading is (#6618). */
const PendingReviewers = styled("div")(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 0.5, 2),
}));
const HeaderText = styled("div")(() => ({
  flex: "1 1 auto",
  minWidth: 0,
}));
const Title = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1.4,
})) as typeof Typography;
// The field and its directory, which stays open while focus is anywhere in here.
const AddArea = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  "&&": {
    flexShrink: 1,
  },
  "& > *": {
    flexShrink: 0,
  },
}));
const SearchRow = styled("div")(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
  // The field has a clear button of its own; a search input's native one would make two.
  "& input::-webkit-search-cancel-button, & input::-webkit-search-decoration": {
    WebkitAppearance: "none",
  },
})) as typeof TextField;
const DoneButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  minWidth: 0,
}));
/**
 * The directory, shown only while someone is using the add field. It sits in the popover's own flow
 * rather than floating over the roster, so nothing in the popover is ever painted over — and it keeps
 * its own scroll, so a long directory cannot push the roster and the footer out of reach.
 *
 * Full width under its label, and ruled off from the roster below, rather than a bordered box inset
 * under the field: a box put the directory's avatars 17px to the right of the roster's, and its rows at
 * a different height, so the same person moved when they were added.
 */
const ResultsList = styled(List)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  maxHeight: REVIEWER_LIST_MAX_HEIGHT,
  minHeight: REVIEWER_ROW_MIN_HEIGHT,
  overflowX: "hidden",
  overflowY: "auto",
  padding: 0,
  "&&": {
    flexShrink: 1,
  },
}));
// A row's height, so a list holding only a message is no shorter than the least it can shrink to.
const ResultsMessage = styled(ListItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  justifyContent: "center",
  minHeight: REVIEWER_ROW_MIN_HEIGHT,
}));
const ResultButton = styled(ListItemButton)(({ theme }) => ({
  gap: theme.spacing(REVIEWER_ROW_GAP),
  minHeight: REVIEWER_ROW_MIN_HEIGHT,
  padding: theme.spacing(1, 2),
}));
const ResultAddIcon = styled(AddIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));
// The source icon leads the result's second line, so it sits under the name without costing a column.
const ResultSecondary = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(0.5),
  minWidth: 0,
  "& > span": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiSvgIcon-root": {
    flexShrink: 0,
    fontSize: "1rem",
  },
}));
// What the two source icons mean, once, under the list rather than on every row.
const DirectoryKey = styled("div")(({ theme }) => ({
  ...theme.typography.caption,
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5, 2),
  padding: theme.spacing(0.75, 2),
}));
const DirectoryKeyItem = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
}));

const CANDIDATE_SOURCE_LABELS = {
  coworker: "Coworker",
  teamMember: "On the vendor team",
} as const;

function renderCandidateSource(candidate: ProposalReviewerCandidate) {
  const label = CANDIDATE_SOURCE_LABELS[candidate.source];
  return candidate.isCoworker ? (
    <BusinessOutlinedIcon titleAccess={label} />
  ) : (
    <GroupsOutlinedIcon titleAccess={label} />
  );
}
// "Add to this proposal" over the directory and "On this proposal" over the roster: with only the
// roster labelled, the directory's rows read as more reviewers until you noticed their plus signs.
//
// Each is the heading of its list and names it, in the popover's weight and colour but at caption
// size: light grey text disappeared between the directory's rule and the first roster row, and a larger
// heading would compete with "Reviewers". Not upper-cased — Done, Discard and the commit already are.
const SectionLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  display: "block",
  fontWeight: theme.typography.fontWeightMedium,
  padding: theme.spacing(1, 2, 0),
})) as typeof Typography;
// Under the directory's rule the roster's heading starts a new section, so it gets the room a section
// does rather than reading as the tail of the list above it.
const SectionLabelAfterDirectory = styled(SectionLabel)(({ theme }) => ({
  paddingTop: theme.spacing(2),
})) as typeof Typography;
// The add field's own bottom padding already separates it from the field.
const DirectoryLabel = styled(SectionLabel)(() => ({
  paddingTop: 0,
})) as typeof Typography;
// The margin makes the last row's own 8px up to the 16px the popover keeps at its edges and around
// its sections: without it the roster sat cramped against the footer's rule, and with no footer the
// popover looked cut off at the bottom.
const RosterList = styled(List)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  maxHeight: REVIEWER_LIST_MAX_HEIGHT,
  minHeight: REVIEWER_ROW_MIN_HEIGHT,
  padding: 0,
  overflowX: "hidden",
  overflowY: "auto",
  "&&": {
    flexShrink: 1,
  },
}));
const RosterItem = styled(ListItem)(({ theme }) => ({
  gap: theme.spacing(REVIEWER_ROW_GAP),
  minHeight: REVIEWER_ROW_MIN_HEIGHT,
  padding: theme.spacing(1, 1, 1, 2),
  "& + &": {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));
const RosterAvatar = styled(ListItemAvatar)(() => ({
  flexShrink: 0,
  minWidth: 0,
}));
const RosterText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== "removed",
})<{ removed?: boolean }>(({ removed, theme }) => ({
  margin: 0,
  minWidth: 0,
  "& .MuiListItemText-primary": {
    color: removed ? theme.palette.text.disabled : theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    overflow: "hidden",
    textDecoration: removed ? "line-through" : "none",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
const RosterSecondary = styled("span")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.25),
  minWidth: 0,
}));
type ReviewerStatusTone = "neutral" | "pending" | "approved";
const StatusLine = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: ReviewerStatusTone }>(({ tone, theme }) => {
  const colorByTone: Record<ReviewerStatusTone, string> = {
    approved: theme.palette.success.main,
    neutral: theme.palette.text.secondary,
    // Amber rather than the primary blue it used to be, which is the colour of everything clickable
    // in this popover and made a status read as a link.
    pending: theme.palette.warning.dark,
  };

  return {
    alignItems: "center",
    color: colorByTone[tone],
    display: "flex",
    fontSize: theme.typography.caption.fontSize,
    gap: theme.spacing(0.5),
    lineHeight: 1.4,
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  };
});
// Two lines before it is cut, rather than one: a single line showed a few words of a note and nothing
// on the row said the rest was a hover away. The tooltip still carries the whole of a longer one.
const NoteLine = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "-webkit-box",
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.4,
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
const NoteTooltipTitle = styled("span")(() => ({
  whiteSpace: "pre-wrap",
}));
const AccessSelect = styled(Select)(({ theme }) => ({
  flexShrink: 0,
  fontSize: theme.typography.body2.fontSize,
  width: "8.5rem",
  "& .MuiSelect-select": {
    paddingBottom: theme.spacing(0.75),
    paddingTop: theme.spacing(0.75),
  },
}));
const RowActionButton = styled(IconButton)(() => ({
  flexShrink: 0,
}));
const EmptyRoster = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  padding: theme.spacing(3, 3, 3.5),
  textAlign: "center",
}));
const EmptyRosterIcon = styled("span")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.action.hover,
  borderRadius: "50%",
  color: theme.palette.text.secondary,
  display: "inline-flex",
  height: "2.5rem",
  justifyContent: "center",
  marginBottom: theme.spacing(0.5),
  width: "2.5rem",
}));
const Footer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "grid",
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5, 2),
}));
const FooterActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const FooterSummary = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flex: "1 1 8rem",
  minWidth: 0,
})) as typeof Typography;
// Underlined, dotted, as text with more behind it is: the counts are a hover (or a tap) away from
// the names they count.
const SummaryText = styled("span")(({ theme }) => ({
  cursor: "default",
  textDecorationColor: theme.palette.text.disabled,
  textDecorationLine: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: "0.25em",
  "&:focus-visible": {
    borderRadius: theme.shape.borderRadius,
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));
const ChangeList = styled("ul")(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2),
}));
const EMPTY_USER_COWORKERS: UserCoworker[] = [];

type ReviewerSelectorProps = {
  proposal?: Proposal;
  popoverAnchor?: HTMLButtonElement;
  reviewerSource?: "proposal" | "proposalBuilder";
  reviewerBaseline?: ProposalReviewer[];
  /**
   * The vendor team as the host page has it, unsaved edits included. A saved draft queues its team
   * edits rather than writing them to the stored builder, so the popover cannot read them there (#5539).
   * Omitted, it reads the stored builder, then the proposal.
   */
  vendorTeam?: WorkAgreement["vendorTeam"];
  onReviewersChanged?: (reviewers: ProposalReviewer[]) => void;
  onPopoverClose?: () => void;
  /**
   * Whether this actor may ask a reviewer to review again. The same capability that governs
   * assigning reviewers (`canManageReviewers`), and false whenever the proposal has no
   * collaboration envelope -- a legacy proposal has no event stream to record the request in.
   */
  canRequestReReview?: boolean;
  /** Fires immediately on click; the caller issues the command. */
  onRequestReReview?: (reviewer: ProposalReviewer) => void;
  /** Reviewer user IDs with a re-review request in flight, so their row can show progress. */
  reReviewPendingUserIds?: ReadonlySet<string>;
  /**
   * The reviewers who have not reviewed what the leader would send, with a reminder for each — the
   * list the submit dialog draws, here so reminding does not have to start with submitting (#6618).
   * Drawn under the heading; `PendingReviewerReminders` draws nothing when nobody is pending.
   */
  pendingReviewerReminders?: React.ReactNode;
  /**
   * Persists the staged roster from inside this popover (#4842), which the page header's Save cannot do
   * while the popover is open. Omit it and no commit button renders. `confirmSave` says whether the press
   * is carrying unsaved proposal content that the actor should be asked about first (#5134).
   */
  onSendToReviewers?: (args: { confirmSave: boolean }) => void;
  /**
   * The host page's unsaved-changes signal — its `isDirty`. It does not gate the commit button (#5134);
   * it decides whether the press confirms before it saves.
   */
  proposalHasUnsavedChanges?: boolean;
  /** True while that save is in flight, so the commit button shows progress and cannot be pressed twice. */
  isSendingToReviewers?: boolean;
};

/** The more-options menu, open for one reviewer and anchored to that reviewer's button. */
type ReviewerMenuTarget = {
  anchor: HTMLElement;
  userId: string;
};

/** Something that can be done to one reviewer from their row. */
type ReviewerRowAction = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
};

type RosterRow = {
  reviewer: ProposalReviewer;
  /** On the persisted roster but staged for removal, shown struck through with a way to keep them. */
  isRemoved: boolean;
};

const SUMMARY_CLAUSE_SEPARATOR = " · ";

/**
 * The footer summary with each clause held on one line, so a narrow footer wraps it between clauses
 * ("1 to notify ·" / "1 access change") rather than inside one ("1 access" / "change").
 */
function keepClausesWhole(summary: string): string {
  return summary
    .split(SUMMARY_CLAUSE_SEPARATOR)
    .map((clause) => clause.replaceAll(" ", "\u00a0"))
    .join(SUMMARY_CLAUSE_SEPARATOR);
}

function getReviewerWithCoworkerIdentity(
  reviewer: ProposalReviewer,
  reviewerCoworkers: UserCoworker[],
): ProposalReviewer {
  const coworker = reviewerCoworkers.find((currentCoworker) => currentCoworker.userId.isEqualTo(reviewer.userId));

  return coworker ? ProposalReviewer.createReviewerFromCoworker(coworker, reviewer) : reviewer.clone();
}

function normalizeReviewersForSelector(
  reviewers: ProposalReviewer[],
  reviewerCoworkers: UserCoworker[],
): ProposalReviewer[] {
  return reviewers.map((reviewer) => getReviewerWithCoworkerIdentity(reviewer, reviewerCoworkers));
}

function getEligibleReviewerCoworkers(coworkers: UserCoworker[], currentUserId?: string): UserCoworker[] {
  return coworkers.filter((coworker) => coworker.userId.value !== currentUserId);
}

function mergeCoworkersByUserId(...coworkerGroups: UserCoworker[][]): UserCoworker[] {
  const coworkersByUserId = new Map<string, UserCoworker>();
  coworkerGroups.flat().forEach((coworker) => coworkersByUserId.set(coworker.userId.value, coworker));
  return [...coworkersByUserId.values()];
}

function findExistingReviewer(reviewers: ProposalReviewer[], coworker: UserCoworker): ProposalReviewer | undefined {
  return reviewers.find((reviewer) => reviewer.userId.isEqualTo(coworker.userId));
}

function createReviewerPool(coworkers: UserCoworker[], proposalReviewers: ProposalReviewer[]): ProposalReviewer[] {
  return coworkers.map((coworker) =>
    ProposalReviewer.createReviewerFromCoworker(coworker, findExistingReviewer(proposalReviewers, coworker)),
  );
}

function filterReviewerPool(
  reviewerPool: ProposalReviewer[],
  reviewerCoworkers: UserCoworker[],
  searchTerm: string,
): ProposalReviewer[] {
  if (searchTerm === "") return reviewerPool;

  const normalizedSearchTerm = searchTerm.toLowerCase();
  return reviewerPool.filter((reviewer) => {
    const coworker = reviewerCoworkers.find((currentCoworker) =>
      currentCoworker.userId.isEqualTo(reviewer.userId),
    );

    return (
      coworker?.name.fullName.toLowerCase().includes(normalizedSearchTerm) === true ||
      coworker?.email.toString().toLowerCase().includes(normalizedSearchTerm) === true
    );
  });
}

/**
 * The single authoritative baseline this selector renders from when there is no in-progress local
 * change to show instead: the persisted (server-acknowledged) reviewer for anyone persisted already
 * knows about, and the builder's own entry for a reviewer that exists only there — a reviewer picked
 * on a fresh draft before its first save, for instance, which persisted has no way to know about yet.
 *
 * Persisted wins whenever both sources know the same reviewer. This is what makes a reviewer's
 * already-granted access render correctly the moment the popover opens even when the local draft
 * builder has not rebased onto that grant (see the "builder trails" landmine below) — the builder is
 * never trusted over persisted for a reviewer both sides already carry.
 */
function mergeReviewerBaseline(
  persistedReviewers: ProposalReviewer[],
  builderReviewers: ProposalReviewer[],
): ProposalReviewer[] {
  const persistedUserIds = new Set(persistedReviewers.map((reviewer) => reviewer.userId.value));
  const builderOnlyReviewers = builderReviewers.filter((reviewer) => !persistedUserIds.has(reviewer.userId.value));

  return [...persistedReviewers, ...builderOnlyReviewers];
}

/**
 * True when the persisted baseline has become exactly the selection this editing session staged —
 * that is, the save carrying the staged change was acknowledged by the server. Checked against
 * persisted alone: the local draft builder is not authoritative (it can trail an acknowledged grant
 * indefinitely — see `mergeReviewerBaseline`), so requiring it to agree too would leave a genuinely
 * acknowledged change staged forever whenever the builder never catches up.
 *
 * Only the three dimensions this selector can change are compared: membership, edit rights, and the
 * custom message. `dateApproved` is server-owned (reviewer access changes deliberately do not reset an
 * endorsement), so including it would make an acknowledgement unrecognizable.
 */
function reviewerSelectionIsAcknowledged(
  persistedReviewers: ProposalReviewer[],
  stagedReviewers: ProposalReviewer[],
): boolean {
  if (persistedReviewers.length !== stagedReviewers.length) return false;

  return stagedReviewers.every((staged) => {
    const persisted = persistedReviewers.find((reviewer) => reviewer.userId.isEqualTo(staged.userId));

    return (
      persisted !== undefined &&
      persisted.isEditing === staged.isEditing &&
      (persisted.customMessage ?? "") === (staged.customMessage ?? "")
    );
  });
}

/**
 * The reviewers popover: who is on this proposal, what each of them may do, and a way to add
 * somebody — in that order of importance, which is the order it is drawn in.
 *
 * It used to be one list: the whole coworker directory with the roster folded into it, a selected
 * reviewer told apart by a tick over their avatar, and an "Allow Editing" switch on every row whether
 * or not that person was a reviewer (turning it on quietly added them). The directory and the roster
 * are now two things. The roster lists the people on the proposal and nobody else, each with their
 * standing ("Approved", "Waiting for review", "Not notified yet") and their access. The directory
 * appears under the add field only while it is being used, and lists only the people who could be
 * added.
 *
 * Nothing here commits on its own. Every edit is staged (see `stagedReviewerSelection`) and goes out
 * through the footer, which exists only while there is something in it to commit — so there is no
 * disabled button to explain — and carries Discard beside the commit. The one exception is a
 * re-review request, which is not proposal content and fires immediately.
 */
export default function ReviewerSelector(props: Readonly<ReviewerSelectorProps>) {
  const { proposal, popoverAnchor, reviewerBaseline, onReviewersChanged, onPopoverClose } = props;
  const canRequestReReview = props.canRequestReReview ?? false;
  const onRequestReReview = props.onRequestReReview;
  const reReviewPendingUserIds = props.reReviewPendingUserIds;
  const onSendToReviewers = props.onSendToReviewers;
  const proposalHasUnsavedChanges = props.proposalHasUnsavedChanges ?? false;
  const isSendingToReviewers = props.isSendingToReviewers ?? false;
  const titleId = useId();
  const summaryId = useId();
  const directoryLabelId = useId();
  const rosterLabelId = useId();
  const reviewerSource = props.reviewerSource ?? "proposalBuilder";
  const session = useSession();
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.down("md"));
  const resultsListRef = React.useRef<HTMLUListElement>(null);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposal?.id ?? Guid.EMPTY));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({
      json: updatedBuilderJSON,
      session,
    });
  }, [session, updatedBuilderJSON]);

  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState<string>("");
  const [searchIsActive, setSearchIsActive] = React.useState(false);
  // Whether the add field is drawn at all. The popover opens on the roster (or its empty state) and an
  // "Add reviewer" button, and the field appears only once that is pressed — with nobody on the roster
  // too, where opening straight into a focused search with its directory spread out was more than the
  // actor had asked for. It is put away again once somebody is added, or on Done or Escape.
  const [addFieldIsOpen, setAddFieldIsOpen] = React.useState(false);
  // Set as the field is put away, so the "Add reviewer" button that replaces it takes focus as it
  // mounts rather than focus falling out of the popover with the field.
  const focusAddButtonOnMount = React.useRef(false);
  // The add field is put away as the popover closes, and during render rather than in an effect, so the
  // first render of the next opening already draws the roster and never mounts a field that would take
  // focus and vanish. React's pattern for adjusting state when a prop changes.
  const [observedPopoverAnchor, setObservedPopoverAnchor] = React.useState(popoverAnchor);
  if (popoverAnchor !== observedPopoverAnchor) {
    setObservedPopoverAnchor(popoverAnchor);
    if (popoverAnchor === undefined) setAddFieldIsOpen(false);
  }
  const [menuTarget, setMenuTarget] = React.useState<ReviewerMenuTarget | null>(null);
  const [noteEditor, setNoteEditor] = React.useState<{ anchor: HTMLElement; reviewer: ProposalReviewer } | null>(null);
  // The note every reviewer about to be asked is sent unless they have a separate one. Held here rather than
  // derived on each render, because the reviewers' messages are what it is written INTO: deriving it
  // back out of them would let overriding enough reviewers silently rename it. It is recovered from
  // those messages each time the popover opens (see the open effect), which is what survives a reload.
  const [sharedNote, setSharedNote] = React.useState("");
  // What is being typed into the shared note, committed to the reviewers on blur rather than per
  // keystroke — each commit restages the roster and writes it through to durable storage.
  const [sharedNoteDraft, setSharedNoteDraft] = React.useState<string>();
  const popoverWasOpen = React.useRef(false);
  // The one piece of state this selector owns: the whole desired reviewer selection this session has
  // explicitly staged (via a click/toggle/message edit), or undefined when nothing is staged and the
  // rendered rows should simply reflect the authoritative baseline below. It survives every popover
  // close/reopen — cleared only by the acknowledgement block further down or by Discard, never by a
  // re-render or a reopen — so a just-made toggle can never revert on its own (see git history for the
  // bug this replaced: an unconditional reset-on-reopen that discarded a staged change the builder had
  // not yet echoed).
  //
  // S4: it is also mirrored to the durable uncommitted-input store (keyed by proposal id, same durable
  // home as the comment composer draft) so a proposal-page remount or a full reload restores it, not
  // just a popover close/reopen. Lazily seeded from that store on mount so a remounted selector starts
  // from whatever was last staged rather than from nothing.
  const uncommittedInputScopeKey = proposalUncommittedInputScopeKey(session);
  const [stagedReviewerSelection, setStagedReviewerSelectionState] = React.useState<ProposalReviewer[] | undefined>(
    () => readStagedReviewerSelection(uncommittedInputScopeKey, proposal?.id),
  );
  const setStagedReviewerSelection = useCallback(
    (reviewers: ProposalReviewer[] | undefined) => {
      setStagedReviewerSelectionState(reviewers);
      if (reviewers === undefined) clearStagedReviewerSelection(uncommittedInputScopeKey, proposal?.id);
      else writeStagedReviewerSelection(uncommittedInputScopeKey, proposal?.id, reviewers);
    },
    [uncommittedInputScopeKey, proposal?.id],
  );
  const builderReviewers = useMemo(() => {
    if (!popoverAnchor) return [];
    const currentProposalReviewers = session.context?.viewingAsVendor
      ? proposal?.vendorReviewers
      : proposal?.clientReviewers;
    if (reviewerSource === "proposal") return currentProposalReviewers ?? [];

    return session.context?.viewingAsVendor
      ? (updatedBuilder?.currentSpec.vendorReviewers?.map((r) => new ProposalReviewer(r)) ??
          currentProposalReviewers ??
          [])
      : (updatedBuilder?.currentSpec.clientReviewers?.map((r) => new ProposalReviewer(r)) ??
          currentProposalReviewers ??
          []);
  }, [
    session.context?.viewingAsVendor,
    popoverAnchor,
    reviewerSource,
    updatedBuilder?.currentSpec.vendorReviewers,
    updatedBuilder?.currentSpec.clientReviewers,
    proposal?.vendorReviewers,
    proposal?.clientReviewers,
  ]);
  const persistedReviewers = useMemo(() => {
    if (!popoverAnchor) return [];

    const currentProposalReviewers = session.context?.viewingAsVendor
      ? proposal?.vendorReviewers
      : proposal?.clientReviewers;
    return reviewerBaseline ?? currentProposalReviewers ?? [];
  }, [
    session.context?.viewingAsVendor,
    popoverAnchor,
    reviewerBaseline,
    proposal?.vendorReviewers,
    proposal?.clientReviewers,
  ]);
  const reviewerIdentityUserIds = useMemo(() => {
    const userIds = new Set<string>();
    [...builderReviewers, ...persistedReviewers].forEach((reviewer) => userIds.add(reviewer.userId.value));

    return userIds;
  }, [builderReviewers, persistedReviewers]);
  const reviewerIndividualsQuery = useGetIndividualsByUserIdsQuery(
    reviewerIdentityUserIds.size ? { userIds: [...reviewerIdentityUserIds] } : skipToken,
  );
  const reviewerIndividuals = useMemo(
    () => Individual.fromJSONArray(reviewerIndividualsQuery.data, { allowEmpty: true }) ?? [],
    [reviewerIndividualsQuery.data],
  );
  // #5404: one endpoint names everybody the leader could add — their coworkers and the people they
  // have worked with — and says which each is. A draft that has not been saved yet has no id to ask by.
  const candidateNegotiationId = proposal?.id && !proposal.id.isEqualTo(Guid.EMPTY) ? proposal.id.value : undefined;
  // A vendor may also ask anyone on the vendor team, from any firm, because vendors subcontract. The
  // team is read as the page has it, so someone just put on it can be asked before the team is saved.
  const viewingAsVendor = session.context?.viewingAsVendor === true;
  const storedVendorTeam =
    reviewerSource === "proposalBuilder" && updatedBuilder?.currentSpec?.workAgreement?.vendorTeam !== undefined
      ? updatedBuilder.currentSpec.workAgreement.vendorTeam
      : proposal?.vendorTeam;
  const pageVendorTeam = props.vendorTeam ?? storedVendorTeam;
  const pageVendorTeamMembers = Masked.isPresentNotMasked(pageVendorTeam) ? pageVendorTeam.members : undefined;
  const {
    candidates,
    isLoading,
    isInitialLoading,
    error: candidatesError,
    loadMore: loadMoreCandidates,
    retry: retryCandidates,
  } = useInfiniteProposalReviewerCandidates({
    negotiationId: candidateNegotiationId,
    search: debouncedSearchTerm.trim(),
    asVendor: viewingAsVendor,
    vendorTeamMembers: viewingAsVendor ? pageVendorTeamMembers : undefined,
    skip: popoverAnchor === undefined,
  });
  const candidatesByUserId = useMemo(
    () => new Map(candidates.data.map((candidate) => [candidate.userId.value, candidate])),
    [candidates.data],
  );
  const reviewerPoolCoworkers = useMemo(
    () =>
      getEligibleReviewerCoworkers(
        candidates.data.map((candidate) => candidate.person),
        session.user?.id?.value,
      ),
    [candidates.data, session.user?.id?.value],
  );
  const storedBaselineReviewerCoworkers =
    useSelector(getUserCoworkersByUserIds(reviewerIdentityUserIds)) ?? EMPTY_USER_COWORKERS;
  const reviewerNormalizationCoworkers = useMemo(
    () => mergeCoworkersByUserId(reviewerPoolCoworkers, storedBaselineReviewerCoworkers),
    [reviewerPoolCoworkers, storedBaselineReviewerCoworkers],
  );
  // `originalReviewers` is the pure persisted baseline (never merged with the builder) — it is the
  // diff target the footer's summary and the "Not notified yet" status compare against, so it must stay
  // exactly what the server last acknowledged. `selectedReviewers` is what actually renders: the staged
  // selection while one is in progress, otherwise the merged baseline (persisted, filled out with any
  // builder-only reviewer — see `mergeReviewerBaseline`). Both are plain derivations of props/state,
  // recomputed every render; nothing here needs an effect to "reseed" on open/close, which removes the
  // whole class of bug where a reopen raced a source that had not yet caught up.
  const originalReviewers = useMemo(
    () => normalizeReviewersForSelector(persistedReviewers, reviewerNormalizationCoworkers),
    [persistedReviewers, reviewerNormalizationCoworkers],
  );
  const mergedBaselineReviewers = useMemo(() => {
    const normalizedBuilderReviewers = normalizeReviewersForSelector(builderReviewers, reviewerNormalizationCoworkers);
    return mergeReviewerBaseline(originalReviewers, normalizedBuilderReviewers);
  }, [builderReviewers, originalReviewers, reviewerNormalizationCoworkers]);
  const selectedReviewers = stagedReviewerSelection ?? mergedBaselineReviewers;
  const selectedReviewerUserIds = useMemo(
    () => new Set([...selectedReviewers, ...originalReviewers].map((reviewer) => reviewer.userId.value)),
    [originalReviewers, selectedReviewers],
  );
  const storedSelectedReviewerCoworkers =
    useSelector(getUserCoworkersByUserIds(selectedReviewerUserIds)) ?? EMPTY_USER_COWORKERS;
  const reviewerDisplayCoworkers = useMemo(
    () => mergeCoworkersByUserId(reviewerNormalizationCoworkers, storedSelectedReviewerCoworkers),
    [reviewerNormalizationCoworkers, storedSelectedReviewerCoworkers],
  );
  // Seeded from the merged baseline rather than the builder alone, so a coworker row opens carrying the
  // access the server has already acknowledged even when the local draft builder still trails it.
  const reviewerPool = useMemo(
    () => createReviewerPool(reviewerPoolCoworkers, mergedBaselineReviewers.map((reviewer) => reviewer.clone())),
    [mergedBaselineReviewers, reviewerPoolCoworkers],
  );

  // Presentation-only: each time the popover is freshly opened, clear the search box and read the
  // shared note back off the staged reviewers. Selection state is deliberately untouched here — see
  // the acknowledgement block below for the only thing besides Discard that clears a staged change.
  useEffect(() => {
    const popoverIsOpen = popoverAnchor !== undefined;
    const popoverJustOpened = popoverIsOpen && !popoverWasOpen.current;
    popoverWasOpen.current = popoverIsOpen;

    if (popoverJustOpened) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setSearchIsActive(false);
      setSharedNote(inferSharedReviewerNote({ originalReviewers, selectedReviewers }));
      setSharedNoteDraft(undefined);
    }
  }, [originalReviewers, popoverAnchor, selectedReviewers]);

  // The only place a staged reviewer selection is ever cleared besides Discard: the persisted baseline
  // has become exactly what this session staged, i.e. the save carrying it was acknowledged. This does
  // not wait on the local draft builder too — see `reviewerSelectionIsAcknowledged` — so a staged change
  // survives every close/reopen and every unrelated re-render (a counterparty edit, a realtime refresh
  // that races the save) until the server actually agrees with it, and never reverts on its own.
  //
  // Adjusted during render rather than in an effect (React's documented pattern for reacting to a prop
  // change without an extra render pass — see "you might not need an effect"): the check only needs to
  // run when `persistedReviewers` itself has actually changed, which a ref comparison detects without
  // the cascading-render an effect+setState here would cause.
  //
  // Gated on the popover being open: `persistedReviewers` is deliberately emptied while closed (see its
  // own memo above), and that collapse is itself a "persistedReviewers changed" transition — comparing
  // a staged selection against that empty stand-in, rather than simply not looking, could clear it for
  // the wrong reason (an empty staged selection would read as "acknowledged" against an empty array on
  // every close, whether or not the server agrees with it).
  const previouslyObservedPersistedReviewers = React.useRef(persistedReviewers);
  if (persistedReviewers !== previouslyObservedPersistedReviewers.current) {
    previouslyObservedPersistedReviewers.current = persistedReviewers;
    if (
      stagedReviewerSelection &&
      popoverAnchor !== undefined &&
      reviewerSelectionIsAcknowledged(persistedReviewers, stagedReviewerSelection)
    ) {
      setStagedReviewerSelection(undefined);
    }
  }

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => globalThis.clearTimeout(timeoutId);
  }, [searchTerm]);

  const addedReviewerUserIds = useMemo(() => {
    const originalReviewerUserIds = new Set(originalReviewers.map((reviewer) => reviewer.userId.value));
    return new Set(
      selectedReviewers
        .filter((reviewer) => !originalReviewerUserIds.has(reviewer.userId.value))
        .map((reviewer) => reviewer.userId.value),
    );
  }, [originalReviewers, selectedReviewers]);
  // Who the next Send asks to review, and so who may be given a note: the reviewers added above, and a
  // reviewer carried on to a revision whom nobody has asked yet (#7083).
  const noteRecipientUserIds = useMemo(
    () => getReviewRequestRecipientUserIds({ originalReviewers, selectedReviewers }),
    [originalReviewers, selectedReviewers],
  );
  // The roster: everyone on the proposal, then anyone the server still has whom this session is about
  // to remove. A pending removal stays on screen, struck through, so the footer's "1 to remove" points
  // at somebody and the actor can change their mind without searching for them again.
  const rosterRows = useMemo<RosterRow[]>(() => {
    const selectedUserIds = new Set(selectedReviewers.map((reviewer) => reviewer.userId.value));
    const removedReviewers = originalReviewers.filter((reviewer) => !selectedUserIds.has(reviewer.userId.value));

    return [
      ...selectedReviewers.map((reviewer) => ({ reviewer, isRemoved: false })),
      ...removedReviewers.map((reviewer) => ({ reviewer, isRemoved: true })),
    ];
  }, [originalReviewers, selectedReviewers]);
  const reviewerCoworkersByUserId = useMemo(
    () => new Map(reviewerDisplayCoworkers.map((coworker) => [coworker.userId.value, coworker])),
    [reviewerDisplayCoworkers],
  );
  const reviewerIndividualsByUserId = useMemo(
    () => new Map(reviewerIndividuals.map((individual) => [individual.userId.value, individual])),
    [reviewerIndividuals],
  );
  // The directory the add field offers: the coworker pool, filtered by the search, less everybody the
  // roster already shows — including a pending removal, whom the roster's own Undo puts back.
  const searchResults = useMemo(() => {
    const rosterUserIds = new Set(rosterRows.map((row) => row.reviewer.userId.value));
    return filterReviewerPool(reviewerPool, reviewerPoolCoworkers, searchTerm === "" ? "" : debouncedSearchTerm).filter(
      (reviewer) => !rosterUserIds.has(reviewer.userId.value),
    );
  }, [debouncedSearchTerm, reviewerPool, reviewerPoolCoworkers, rosterRows, searchTerm]);
  const sendActionState = getReviewerSendActionState({
    originalReviewers,
    selectedReviewers,
    proposalHasUnsavedChanges,
  });
  const separateNoteCount = selectedReviewers.filter(
    (reviewer) => noteRecipientUserIds.has(reviewer.userId.value) && !followsSharedReviewerNote(reviewer, sharedNote),
  ).length;
  const menuReviewer = menuTarget
    ? selectedReviewers.find((reviewer) => reviewer.userId.value === menuTarget.userId)
    : undefined;
  const showSearchResults = searchIsActive;

  function getReviewerName(reviewer: ProposalReviewer): string {
    const coworker = reviewerCoworkersByUserId.get(reviewer.userId.value);
    const individual = reviewerIndividualsByUserId.get(reviewer.userId.value);
    return coworker?.name.fullName ?? individual?.fullName ?? "Unknown reviewer";
  }

  function getReviewerIndividual(reviewer: ProposalReviewer): Individual | undefined {
    const coworker = reviewerCoworkersByUserId.get(reviewer.userId.value);
    return coworker?.asIndividual() ?? reviewerIndividualsByUserId.get(reviewer.userId.value);
  }

  function stageReviewers(updatedReviewers: ProposalReviewer[]) {
    setStagedReviewerSelection(updatedReviewers);
    onReviewersChanged?.(updatedReviewers);
  }

  function handlePopoverClosed() {
    onPopoverClose?.();
  }

  function search(value: string) {
    setSearchTerm(value);

    if (value === "") {
      setDebouncedSearchTerm("");
    }
  }

  // The directory stays open while focus is anywhere inside the add area — the field or one of its
  // results — and closes the moment focus leaves it, which is what a result being clicked or tabbed to
  // must not count as.
  function handleAddAreaBlurred(event: React.FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setSearchIsActive(false);
  }

  // Done, Escape, or somebody just added: the directory closes without anybody having to click
  // somewhere else to lose it, the field goes with it, and focus goes to the "Add reviewer" button that
  // brings it back.
  function closeDirectory() {
    search("");
    setSearchIsActive(false);
    focusAddButtonOnMount.current = true;
    setAddFieldIsOpen(false);
  }

  function handleAddFieldOpened() {
    setAddFieldIsOpen(true);
  }

  function focusAddButtonWhenFieldIsPutAway(node: HTMLButtonElement | null) {
    if (!node || !focusAddButtonOnMount.current) return;

    focusAddButtonOnMount.current = false;
    node.focus();
  }

  // Escape closes the directory before it closes the popover, as it would any other list opened from a
  // field; a second Escape then closes the popover. Returns whether it did, so a caller can stop there.
  function closeDirectoryOnEscape(event: React.KeyboardEvent<HTMLElement>): boolean {
    if (event.key !== "Escape" || !searchIsActive) return false;

    event.stopPropagation();
    closeDirectory();
    return true;
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (closeDirectoryOnEscape(event) || event.key !== "ArrowDown") return;

    const firstResult = resultsListRef.current?.querySelector<HTMLElement>(".MuiListItemButton-root");
    if (!firstResult) return;
    event.preventDefault();
    firstResult.focus();
  }

  function handleResultsScrolled(event: React.UIEvent<HTMLUListElement>) {
    const list = event.currentTarget;
    if (list.scrollHeight - list.clientHeight - list.scrollTop > RESULTS_LOAD_MORE_THRESHOLD_PX) return;
    if (candidates.hasNextPage && !isLoading) loadMoreCandidates();
  }

  // The search takes focus as "Add reviewer" draws it, the only way it is ever drawn, and opens its
  // directory. A callback ref rather than `autoFocus`, so it runs as the field mounts and not on every
  // render the field is kept through.
  const focusSearchWhenShown = useCallback((node: HTMLInputElement | null) => {
    if (!node) return;

    node.focus();
    setSearchIsActive(true);
  }, []);

  // The search closes once somebody is picked: the new row, its access and its note are what the actor
  // looks at next, and the directory would otherwise sit over them. Adding another is "Add reviewer"
  // again.
  function handleReviewerAdded(reviewer: ProposalReviewer) {
    // A reviewer added after the shared note was written gets it too, as they would have had they been
    // added first.
    const addedReviewer = setReviewerNote(reviewer, sharedNote);
    stageReviewers(toggleReviewerSelection({ selectedReviewers, reviewer: addedReviewer }));
    closeDirectory();
  }

  function handleRemovalUndone(reviewer: ProposalReviewer) {
    stageReviewers([...selectedReviewers, reviewer]);
  }

  function handleAccessChanged(reviewer: ProposalReviewer, access: string) {
    const selectedReviewer = selectedReviewers.find((current) => current.userId.isEqualTo(reviewer.userId));
    if (!selectedReviewer || selectedReviewer.isEditing === (access === EDIT_ACCESS)) return;

    stageReviewers(toggleReviewerEditing({ selectedReviewers, reviewer }));
  }

  function handleMenuOpened(event: React.MouseEvent<HTMLButtonElement>, reviewer: ProposalReviewer) {
    setMenuTarget({ anchor: event.currentTarget, userId: reviewer.userId.value });
  }

  function handleMenuClosed() {
    setMenuTarget(null);
  }

  function handleReviewerRemoved(reviewer: ProposalReviewer) {
    setMenuTarget(null);
    stageReviewers(removeReviewer({ selectedReviewers, reviewer }));
  }

  // Deliberately NOT staged behind the footer's commit, unlike every roster edit above it. A
  // re-review request changes no proposal content -- it dismisses one reviewer's approval -- so
  // there is nothing to stage alongside the roster diff, and holding it back would leave the
  // leader looking at a control that appeared to do nothing.
  function handleReReviewRequested(reviewer: ProposalReviewer) {
    setMenuTarget(null);
    onRequestReReview?.(reviewer);
  }

  function handleSeparateNoteOpened(reviewer: ProposalReviewer) {
    if (menuTarget) setNoteEditor({ anchor: menuTarget.anchor, reviewer });
    setMenuTarget(null);
  }

  function handleSharedNoteRestored(reviewer: ProposalReviewer) {
    setMenuTarget(null);
    stageReviewers(
      selectedReviewers.map((current) =>
        current.userId.isEqualTo(reviewer.userId) ? setReviewerNote(current, sharedNote) : current,
      ),
    );
  }

  function handleReviewerMessageChanged(userId?: Guid, message?: string) {
    setNoteEditor(null);

    if (!userId) return;

    // Emptying a separate note hands the reviewer back to the shared one rather than leaving them with
    // none, which the row would otherwise have no way to tell apart from following it.
    const note = message?.trim() ? message : sharedNote;
    stageReviewers(
      selectedReviewers.map((reviewer) =>
        reviewer.userId.isEqualTo(userId) ? setReviewerNote(reviewer, note) : reviewer,
      ),
    );
  }

  function handleSharedNoteCommitted() {
    if (sharedNoteDraft === undefined) return;

    const nextNote = sharedNoteDraft.trim();
    setSharedNoteDraft(undefined);
    if (nextNote === sharedNote) return;

    const updatedReviewers = applySharedReviewerNote({
      originalReviewers,
      selectedReviewers,
      previousNote: sharedNote,
      nextNote,
    });
    setSharedNote(nextNote);
    stageReviewers(updatedReviewers);
  }

  // Back to exactly what the server has. The host is told too, because on the draft page the staged
  // roster has already been written into the builder, and the builder is half of what renders here.
  function handleChangesDiscarded() {
    setSharedNote("");
    setSharedNoteDraft(undefined);
    setStagedReviewerSelection(undefined);
    onReviewersChanged?.(originalReviewers);
  }

  function renderStatus(row: RosterRow, reviewerName: string) {
    const { reviewer, isRemoved } = row;

    if (isRemoved) return <StatusLine tone="neutral">Will be removed</StatusLine>;
    if (reReviewPendingUserIds?.has(reviewer.userId.value) === true) {
      return (
        <StatusLine tone="neutral">
          <CircularProgress aria-label={`Requesting re-review from ${reviewerName}`} size={12} />
          Asking to review again…
        </StatusLine>
      );
    }
    if (addedReviewerUserIds.has(reviewer.userId.value)) {
      return (
        <StatusLine tone="pending">
          <NotificationsNoneIcon aria-hidden />
          Not notified yet
        </StatusLine>
      );
    }
    if (reviewer.isAwaitingReviewRequest) {
      return (
        <StatusLine tone="pending">
          <NotificationsNoneIcon aria-hidden />
          Not asked to review yet
        </StatusLine>
      );
    }
    if (reviewer.dateApproved) {
      return (
        <StatusLine tone="approved">
          <CheckCircleIcon aria-hidden />
          Approved {reviewer.dateApproved.format("MMM D")}
        </StatusLine>
      );
    }

    return (
      <StatusLine tone="neutral">
        <ScheduleIcon aria-hidden />
        Waiting for review
      </StatusLine>
    );
  }

  function renderNote(row: RosterRow) {
    const { reviewer, isRemoved } = row;
    const note = reviewer.customMessage?.trim() ?? "";
    if (!note || isRemoved) return null;

    // A reviewer about to be asked who follows the shared note shows nothing of it here: the footer
    // shows it once, for everybody. A reviewer already asked shows the note they were actually sent.
    const isAboutToBeAsked = noteRecipientUserIds.has(reviewer.userId.value);
    if (isAboutToBeAsked && followsSharedReviewerNote(reviewer, sharedNote)) return null;

    const label = isAboutToBeAsked ? `Separate note: ${note}` : `Note: ${note}`;
    return (
      <Tooltip enterDelay={600} placement="bottom" title={<NoteTooltipTitle>{label}</NoteTooltipTitle>}>
        <NoteLine>{label}</NoteLine>
      </Tooltip>
    );
  }

  function renderRosterRow(row: RosterRow) {
    const { reviewer, isRemoved } = row;
    const reviewerName = getReviewerName(reviewer);

    return (
      <RosterItem key={reviewer.userId.value}>
        <RosterAvatar>
          <IndividualAvatar individual={getReviewerIndividual(reviewer)} disabled={isRemoved} />
        </RosterAvatar>
        <RosterText
          removed={isRemoved}
          primary={reviewerName}
          secondary={
            <RosterSecondary>
              {renderStatus(row, reviewerName)}
              {renderNote(row)}
            </RosterSecondary>
          }
          slotProps={{ secondary: { component: "span" } }}
        />
        {isRemoved ? (
          <Button size="small" aria-label={`Keep ${reviewerName} as a reviewer`} onClick={() => handleRemovalUndone(reviewer)}>
            Undo
          </Button>
        ) : (
          <>
            <AccessSelect
              size="small"
              value={reviewer.isEditing ? EDIT_ACCESS : COMMENT_ACCESS}
              onChange={(event) => handleAccessChanged(reviewer, String(event.target.value))}
              inputProps={{ "aria-label": `Access for ${reviewerName}` }}
            >
              <MenuItem value={COMMENT_ACCESS}>Can comment</MenuItem>
              <MenuItem value={EDIT_ACCESS}>Can edit</MenuItem>
            </AccessSelect>
            {renderRowActions(reviewer, reviewerName)}
          </>
        )}
      </RosterItem>
    );
  }

  function renderSearchResults() {
    if (!showSearchResults) return null;

    return (
      <>
        <DirectoryLabel id={directoryLabelId} component="h3" variant="caption">
          Add to this proposal
        </DirectoryLabel>
        <ResultsList
          dense
          ref={resultsListRef}
          aria-labelledby={directoryLabelId}
          onScroll={handleResultsScrolled}
          onKeyDown={closeDirectoryOnEscape}
        >
          {isInitialLoading && (
            <ResultsMessage>
              <CircularProgress aria-label="Loading people" disableShrink size={24} thickness={4} />
            </ResultsMessage>
          )}
          {!isInitialLoading && !candidatesError && searchResults.length === 0 && (
            <ResultsMessage>
              <Typography variant="body2">
                {searchTerm.trim() === ""
                  ? "No one else to add."
                  : `No one matches “${searchTerm.trim()}”.`}
              </Typography>
            </ResultsMessage>
          )}
          {!isInitialLoading && searchResults.map(renderSearchResult)}
          {isLoading && !isInitialLoading && (
            <ResultsMessage>
              <CircularProgress aria-label="Loading more people" disableShrink size={20} thickness={4} />
            </ResultsMessage>
          )}
          {candidatesError && (
            <ListItem>
              <Alert
                action={
                  <Button color="inherit" size="small" onClick={retryCandidates}>
                    Retry
                  </Button>
                }
                severity="error"
              >
                Couldn&apos;t load people to add.
              </Alert>
            </ListItem>
          )}
        </ResultsList>
        {!isInitialLoading && searchResults.length > 0 && (
          <DirectoryKey aria-label="What the icons mean">
            <DirectoryKeyItem>
              <BusinessOutlinedIcon aria-hidden />
              {CANDIDATE_SOURCE_LABELS.coworker}
            </DirectoryKeyItem>
            <DirectoryKeyItem>
              <GroupsOutlinedIcon aria-hidden />
              {CANDIDATE_SOURCE_LABELS.teamMember}
            </DirectoryKeyItem>
          </DirectoryKey>
        )}
      </>
    );
  }

  // Each result says where the leader knows them from. Somebody on the other side of this proposal is
  // listed but cannot be picked, and says why: the server refuses anyone who represents both parties.
  function renderSearchResult(reviewer: ProposalReviewer) {
    const coworker = reviewerCoworkersByUserId.get(reviewer.userId.value);
    const candidate = candidatesByUserId.get(reviewer.userId.value);
    const reviewerName = getReviewerName(reviewer);
    const isCounterparty = candidate?.isCounterparty === true;
    const secondary = (
      <ResultSecondary>
        {candidate && renderCandidateSource(candidate)}
        <span>{isCounterparty ? "On the other side of this proposal" : coworker?.email.toString()}</span>
      </ResultSecondary>
    );

    return (
      <ListItem key={reviewer.userId.value} disablePadding>
        <ResultButton
          aria-label={
            isCounterparty
              ? `${reviewerName} is on the other side of this proposal and cannot review it`
              : `Add ${reviewerName} as a reviewer`
          }
          disabled={isCounterparty}
          onClick={() => handleReviewerAdded(reviewer)}
        >
          <RosterAvatar>
            <IndividualAvatar individual={getReviewerIndividual(reviewer)} disabled={isCounterparty} />
          </RosterAvatar>
          <RosterText primary={reviewerName} secondary={secondary} slotProps={{ secondary: { component: "span" } }} />
          {!isCounterparty && <ResultAddIcon fontSize="small" aria-hidden />}
        </ResultButton>
      </ListItem>
    );
  }

  function getRowActions(reviewer: ProposalReviewer): ReviewerRowAction[] {
    const isNew = addedReviewerUserIds.has(reviewer.userId.value);
    const isAboutToBeAsked = noteRecipientUserIds.has(reviewer.userId.value);
    const hasSeparateNote = isAboutToBeAsked && !followsSharedReviewerNote(reviewer, sharedNote);
    const canAskAgain =
      !isNew &&
      canRequestReReview &&
      Boolean(reviewer.dateApproved) &&
      reReviewPendingUserIds?.has(reviewer.userId.value) !== true;
    const actions: ReviewerRowAction[] = [];

    if (isAboutToBeAsked) {
      actions.push({
        key: "note",
        label: hasSeparateNote ? "Edit separate note" : "Write a separate note",
        icon: <EditNoteIcon fontSize="small" />,
        onSelect: () => handleSeparateNoteOpened(reviewer),
      });
    }
    if (hasSeparateNote) {
      actions.push({
        key: "shared-note",
        label: sharedNote ? "Use the shared note" : "Remove the note",
        icon: <ReplayIcon fontSize="small" />,
        onSelect: () => handleSharedNoteRestored(reviewer),
      });
    }
    if (canAskAgain) {
      actions.push({
        key: "re-review",
        label: "Ask to review again",
        icon: <ReplayIcon fontSize="small" />,
        onSelect: () => handleReReviewRequested(reviewer),
      });
    }
    actions.push({
      key: "remove",
      label: "Remove as reviewer",
      icon: <PersonRemoveIcon fontSize="small" />,
      onSelect: () => handleReviewerRemoved(reviewer),
    });

    return actions;
  }

  function renderMenuItems(reviewer: ProposalReviewer) {
    // Removal is not drawn in red: it is staged and undoable until the save, not a deletion.
    return getRowActions(reviewer).map((action) => (
      <MenuItem key={action.key} onClick={action.onSelect}>
        <ListItemIcon>{action.icon}</ListItemIcon>
        {action.label}
      </MenuItem>
    ));
  }

  // A menu of one item put the only thing a row offered two clicks away, behind a button that did not
  // say what it held. So a row with a single action shows that action as its own button: a standing
  // reviewer who has not approved, whose one action is removal. Every other row keeps the menu.
  function renderRowActions(reviewer: ProposalReviewer, reviewerName: string) {
    const actions = getRowActions(reviewer);

    if (actions.length === 1) {
      const [action] = actions;
      return (
        <Tooltip describeChild title={action.label}>
          <RowActionButton
            size="small"
            aria-label={`Remove ${reviewerName} as reviewer`}
            onClick={action.onSelect}
          >
            {action.icon}
          </RowActionButton>
        </Tooltip>
      );
    }

    return (
      <RowActionButton
        size="small"
        aria-label={`More options for ${reviewerName}`}
        aria-haspopup="menu"
        onClick={(event) => handleMenuOpened(event, reviewer)}
      >
        <MoreHorizIcon fontSize="small" />
      </RowActionButton>
    );
  }

  function renderRoster() {
    if (rosterRows.length > 0) {
      const RosterLabel = showSearchResults ? SectionLabelAfterDirectory : SectionLabel;
      return (
        <>
          <RosterLabel id={rosterLabelId} component="h3" variant="caption">
            On this proposal
          </RosterLabel>
          <RosterList dense aria-labelledby={rosterLabelId}>
            {rosterRows.map(renderRosterRow)}
          </RosterList>
        </>
      );
    }

    // With nobody on the roster the directory is open whenever the field has focus, and the empty
    // state would only compete with it for the same space — so it waits until the directory closes.
    if (showSearchResults) return null;

    return (
      <EmptyRoster>
        <EmptyRosterIcon>
          <GroupOutlinedIcon aria-hidden />
        </EmptyRosterIcon>
        <Typography variant="subtitle2" component="p">
          No reviewers yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add someone to review this proposal before you send it.
        </Typography>
      </EmptyRoster>
    );
  }

  function renderFooter() {
    // Nothing to commit, nothing drawn: each row's status already says where every reviewer stands.
    if (!sendActionState.hasChanges) return null;

    const changeDescriptions = describeReviewerChanges({
      originalReviewers,
      selectedReviewers,
      getName: getReviewerName,
    });

    return (
      <Footer>
        {noteRecipientUserIds.size > 0 && (
          <TextField
            // Only "new" while everybody it goes to is new: a reviewer carried on to a revision gets it
            // too, and is not new to the agreement.
            label={noteRecipientUserIds.size === addedReviewerUserIds.size ? "Note to new reviewers" : "Note to reviewers"}
            placeholder="Optional. Sent with the request to review."
            size="small"
            multiline
            maxRows={4}
            value={sharedNoteDraft ?? sharedNote}
            onChange={(event) => setSharedNoteDraft(event.target.value)}
            onBlur={handleSharedNoteCommitted}
            helperText={
              separateNoteCount > 0
                ? `${separateNoteCount} ${separateNoteCount === 1 ? "reviewer gets" : "reviewers get"} a separate note.`
                : undefined
            }
          />
        )}
        <FooterActions>
          <FooterSummary id={summaryId} variant="body2" component="div">
            <Tooltip
              describeChild
              enterTouchDelay={0}
              placement="top-start"
              title={
                <ChangeList aria-label="Changes to save">
                  {changeDescriptions.map((description) => (
                    <li key={description}>{description}</li>
                  ))}
                </ChangeList>
              }
            >
              {/* Focusable so a keyboard can open the list too; a tooltip on bare text is hover-only. */}
              <SummaryText tabIndex={0}>{keepClausesWhole(sendActionState.summary)}</SummaryText>
            </Tooltip>
          </FooterSummary>
          <Button color="inherit" size="small" onClick={handleChangesDiscarded}>
            Discard
          </Button>
          {onSendToReviewers && (
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={sendActionState.addedReviewersCount > 0 ? <SendIcon /> : undefined}
              loading={isSendingToReviewers}
              disabled={isSendingToReviewers}
              aria-describedby={summaryId}
              onClick={() => onSendToReviewers({ confirmSave: sendActionState.confirmsUnsavedProposalChanges })}
            >
              {sendActionState.label}
            </LoadingButton>
          )}
        </FooterActions>
      </Footer>
    );
  }

  function renderAddField() {
    return (
      <AddArea onFocus={() => setSearchIsActive(true)} onBlur={handleAddAreaBlurred}>
        <SearchRow>
          <SearchField
            placeholder={REVIEWER_SEARCH_PLACEHOLDER}
            // It looks people up; a browser or password manager reads "name or email" and offers
            // the actor's own. `off` alone is ignored for a field it has guessed at, so the field is
            // also a search, which browsers do not autofill, and marked for the common managers.
            type="search"
            name="reviewer-search"
            autoComplete="off"
            size="small"
            inputRef={focusSearchWhenShown}
            onKeyDown={handleSearchKeyDown}
            slotProps={{
              htmlInput: {
                // A placeholder is not a name, and a screen reader announced this as an unnamed search.
                "aria-label": "Search for a reviewer",
                "data-1p-ignore": true,
                "data-bwignore": true,
                "data-form-type": "other",
                "data-lpignore": "true",
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm !== "" && (
                      <IconButton size="small" aria-label="Clear reviewer search" onClick={() => search("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    {showSearchResults && (
                      <DoneButton
                        size="small"
                        // Keeps focus in the field until the click lands. A browser that does not focus
                        // a clicked button (Safari) would otherwise blur the field first, and the
                        // directory would close out from under the pointer.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={closeDirectory}
                      >
                        Done
                      </DoneButton>
                    )}
                  </InputAdornment>
                ),
              },
            }}
            value={searchTerm}
            onChange={(event) => search(event.target.value)}
          />
        </SearchRow>
        {renderSearchResults()}
      </AddArea>
    );
  }

  // Where the add field is while it is put away: see `addFieldIsOpen`.
  function renderAddButton() {
    return (
      <SearchRow>
        <Button
          ref={focusAddButtonWhenFieldIsPutAway}
          size="small"
          startIcon={<PersonAddAlt1Icon />}
          onClick={handleAddFieldOpened}
        >
          Add reviewer
        </Button>
      </SearchRow>
    );
  }

  return (
    <ReviewerPopover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      marginThreshold={isMobileDisplay ? 8 : 16}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={handlePopoverClosed}
      slotProps={{ paper: { "aria-labelledby": titleId } }}
    >
      <PopoverContainer>
        <Header>
          <HeaderText>
            <Title id={titleId} component="h2" variant="subtitle1">
              Reviewers
            </Title>
            <Typography variant="body2" color="text.secondary">
              People who review before you send.
            </Typography>
          </HeaderText>
          <IconButton size="small" aria-label="Close reviewers" onClick={handlePopoverClosed}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>
        {props.pendingReviewerReminders && <PendingReviewers>{props.pendingReviewerReminders}</PendingReviewers>}
        {addFieldIsOpen ? renderAddField() : renderAddButton()}
        {renderRoster()}
        {renderFooter()}
      </PopoverContainer>
      <Menu anchorEl={menuTarget?.anchor} open={Boolean(menuTarget && menuReviewer)} onClose={handleMenuClosed}>
        {menuReviewer && renderMenuItems(menuReviewer)}
      </Menu>
      {/*
        Mounted per opening, and keyed by the reviewer it belongs to, so the editor seeds itself from
        THAT reviewer's saved note. Kept mounted across openings, it showed the first reviewer's draft
        for every reviewer opened after them.
      */}
      {noteEditor && (
        <ReviewerMessagePopover
          key={noteEditor.reviewer.userId.value}
          userId={noteEditor.reviewer.userId}
          anchorEl={noteEditor.anchor}
          message={
            followsSharedReviewerNote(noteEditor.reviewer, sharedNote) ? "" : noteEditor.reviewer.customMessage
          }
          onCanceled={() => setNoteEditor(null)}
          onMessageChanged={(userId?: Guid, message?: string) => {
            handleReviewerMessageChanged(userId, message);
          }}
        />
      )}
    </ReviewerPopover>
  );
}
